// apps/dashboard/src/components/layout/version-badge.tsx
// Exibe as versões do frontend (build time) e da API (via /health).
// Usado no popover do usuário no sidebar.

import { useQuery } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Circle, RefreshCw, ServerCrash } from 'lucide-react';

interface HealthResponse {
  status: 'ok' | 'degraded' | 'down';
  version?: string;
  commit?: string;
  service?: string;
}

function fetchApiHealth(): Promise<HealthResponse> {
  return fetch('/health', { signal: AbortSignal.timeout(4000) }).then((r) => r.json());
}

const STATUS_ICON = {
  ok: <CheckCircle2 className="h-2.5 w-2.5 text-emerald-500 shrink-0" />,
  degraded: <AlertCircle className="h-2.5 w-2.5 text-amber-400 shrink-0" />,
  down: <ServerCrash className="h-2.5 w-2.5 text-destructive shrink-0" />,
  loading: <Circle className="h-2.5 w-2.5 text-muted-foreground/30 shrink-0 animate-pulse" />,
};

export function VersionInfo() {
  const { data, isFetching, refetch } = useQuery<HealthResponse>({
    queryKey: ['api-health-version'],
    queryFn: fetchApiHealth,
    // Refresh every 5 minutes — it's just for display, no need to spam
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const apiStatus = isFetching ? 'loading' : (data?.status ?? 'down');
  const apiVersion = data?.version ?? '—';
  const apiCommit = data?.commit ?? '—';

  const feVersion = __APP_VERSION__;
  const feCommit = __BUILD_COMMIT__;

  return (
    <div className="px-2 pt-2 pb-1 border-t border-border/60 mt-1 space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wide">
          Versões
        </span>
        <button
          type="button"
          onClick={() => refetch()}
          className="rounded p-0.5 hover:bg-accent transition-colors"
          title="Verificar versões"
        >
          <RefreshCw
            className={`h-2.5 w-2.5 text-muted-foreground/40 ${isFetching ? 'animate-spin' : ''}`}
          />
        </button>
      </div>

      {/* Frontend */}
      <div className="flex items-center gap-1.5">
        {STATUS_ICON.ok}
        <div className="flex-1 min-w-0">
          <span className="text-[10px] text-muted-foreground/50">Frontend</span>
          <span className="text-[10px] font-mono text-foreground/70 ml-1.5">v{feVersion}</span>
          <span className="text-[9px] font-mono text-muted-foreground/30 ml-1">{feCommit}</span>
        </div>
      </div>

      {/* API */}
      <div className="flex items-center gap-1.5">
        {STATUS_ICON[apiStatus as keyof typeof STATUS_ICON]}
        <div className="flex-1 min-w-0">
          <span className="text-[10px] text-muted-foreground/50">API</span>
          <span className="text-[10px] font-mono text-foreground/70 ml-1.5">
            {isFetching ? '...' : `v${apiVersion}`}
          </span>
          {!isFetching && apiCommit !== '—' && (
            <span className="text-[9px] font-mono text-muted-foreground/30 ml-1">{apiCommit}</span>
          )}
        </div>
      </div>

      {/* Alerta se versões divergem (major.minor) */}
      {!isFetching && data?.version && feVersion !== data.version && (
        <p className="text-[9px] text-amber-500/80 flex items-center gap-1 mt-0.5">
          <AlertCircle className="h-2.5 w-2.5 shrink-0" />
          Versões diferentes — atualize o servidor
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// VersionBadge — badge compacto para o rodapé do sidebar e tela de login
// ---------------------------------------------------------------------------

interface VersionBadgeProps {
  /** Quando true exibe apenas a versão do frontend (sem chamada de rede — útil na tela de login) */
  feOnly?: boolean;
  className?: string;
}

export function VersionBadge({ feOnly = false, className = '' }: VersionBadgeProps) {
  const { data, isFetching } = useQuery<HealthResponse>({
    queryKey: ['api-health-version'],
    queryFn: fetchApiHealth,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    enabled: !feOnly,
  });

  const feVersion = __APP_VERSION__;
  const apiVersion = isFetching ? '…' : (data?.version ?? '—');
  const apiStatus = isFetching ? 'loading' : (data?.status ?? 'down');

  return (
    <div
      className={`flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground/40 select-none ${className}`}
    >
      <span title={`Frontend v${feVersion} · commit ${__BUILD_COMMIT__}`}>fe v{feVersion}</span>
      {!feOnly && (
        <>
          <span className="opacity-40">·</span>
          <span className="flex items-center gap-0.5" title={`API v${apiVersion} · ${apiStatus}`}>
            {STATUS_ICON[apiStatus as keyof typeof STATUS_ICON]}
            api v{apiVersion}
          </span>
        </>
      )}
    </div>
  );
}
